import React, { memo, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Alert, Spin } from 'antd';
import styled from 'styled-components';

import { CareerIcon, CareerIconName } from 'atoms';
import { Colors } from 'context';
import { useCareerLessons, useCareerSectionAccess, useMyCareerCenter } from 'core';
import { CareerLessonRow } from 'molecules';
import { CareerLockedSection } from 'organisms';
import { accent } from 'services/careerAccents';
import { careerLayout, careerType } from 'services/careerType';
import { mobile } from 'services/ui';

/**
 * `/career/learning` — «Przygotowanie do pracy» (ТЗ 0071, п. 5.1).
 *
 * Проверка в Brave: текущий урок терялся среди пройденных, а крупная полоса
 * прогресса повторяла счётчик над ней. Новый порядок:
 *
 *   1. «Ukończono 5 z 7 lekcji» — одно число, с сервера
 *   2. крупная карточка ТЕКУЩЕГО урока
 *   3. следующий урок с условием открытия
 *   4. свёрнутая группа «Ukończone — 5»
 *   5. закладки — вторичным переключателем
 *
 * 🔴 Текущий урок выбирается по `state`, который считает сервер: «начатый»
 * старше «доступного», как в правилах главной кнопки. Своего правила
 * разблокировки у экрана нет.
 */
type LearningTab = 'todo' | 'done' | 'saved';

const TABS: { key: LearningTab; icon: CareerIconName; label: string }[] = [
  { key: 'todo', icon: 'learning', label: 'careerCenter.learning.tabs.todo' },
  { key: 'done', icon: 'check', label: 'careerCenter.learning.tabs.done' },
  { key: 'saved', icon: 'bookmark', label: 'careerCenter.learning.tabs.saved' },
];

const readTab = (value: string | null): LearningTab =>
  value === 'done' || value === 'saved' ? value : 'todo';

export const CareerLearning = memo(() => {
  const { t } = useTranslation();
  const { center } = useMyCareerCenter();
  const { locked, pending } = useCareerSectionAccess('lessons');
  const { lessons, loading, error } = useCareerLessons();
  // Доработка 15.09: вкладки вместо свёрнутых блоков «Ukończone — 5» —
  // владелец не понял, что это кнопки. Вкладка — в адресе: «Назад» и ссылка
  // возвращают туда же.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = readTab(searchParams.get('tab'));
  const selectTab = (next: LearningTab) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'todo') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params);
  };

  const groups = useMemo(() => {
    const current =
      lessons.find((lesson) => lesson.state === 'in_progress') ??
      lessons.find((lesson) => lesson.state === 'not_started') ??
      null;
    return {
      current,
      open: lessons.filter(
        (lesson) =>
          lesson !== current && (lesson.state === 'in_progress' || lesson.state === 'not_started'),
      ),
      locked: lessons.filter((lesson) => lesson.state === 'locked'),
      completed: lessons.filter((lesson) => lesson.state === 'completed'),
      bookmarked: lessons.filter((lesson) => lesson.isBookmarked),
    };
  }, [lessons]);

  if (locked || pending) {
    return (
      <PageWrapper>
        {locked ? <CareerLockedSection capability="lessons" /> : <Spin size="large" />}
      </PageWrapper>
    );
  }

  const counts: Record<LearningTab, number> = {
    todo: (groups.current ? 1 : 0) + groups.open.length + groups.locked.length,
    done: groups.completed.length,
    saved: groups.bookmarked.length,
  };
  const tone = accent('learning');
  const percent =
    center && center.lessonsTotal > 0
      ? Math.round((center.lessonsCompleted / center.lessonsTotal) * 100)
      : 0;

  return (
    <PageWrapper>
      <Header>
        <TitleRow>
          <TitleIcon $bg={tone.bg} $fg={tone.fg} aria-hidden>
            <CareerIcon name="learning" size={28} />
          </TitleIcon>
          <PageTitle>{t('careerCenter.learning.title')}</PageTitle>
        </TitleRow>
        {center && center.lessonsTotal > 0 ? (
          <Progress>
            <Bar aria-hidden>
              <BarFill style={{ width: `${percent}%` }} $fg={tone.fg} />
            </Bar>
            <Counter>
              {t('careerCenter.learning.completedOf', {
                done: center.lessonsCompleted,
                total: center.lessonsTotal,
              })}
            </Counter>
          </Progress>
        ) : null}
      </Header>

      <Tabs role="tablist" aria-label={t('careerCenter.learning.tabs.label')}>
        {TABS.map((item) => (
          <Tab
            key={item.key}
            type="button"
            role="tab"
            id={`learning-tab-${item.key}`}
            aria-selected={tab === item.key}
            aria-controls="learning-panel"
            $active={tab === item.key}
            $bg={tone.bg}
            $fg={tone.fg}
            onClick={() => selectTab(item.key)}>
            <CareerIcon name={item.icon} size={18} />
            {t(item.label)}
            <TabCount>{counts[item.key]}</TabCount>
          </Tab>
        ))}
      </Tabs>

      <Panel id="learning-panel" role="tabpanel" aria-labelledby={`learning-tab-${tab}`}>
        {error ? (
          <Alert type="error" showIcon message={t('careerCenter.learning.loadError')} />
        ) : loading && !lessons.length ? (
          <Center>
            <Spin size="large" />
          </Center>
        ) : !lessons.length ? (
          <Empty>{t('careerCenter.learning.empty')}</Empty>
        ) : tab === 'todo' ? (
          <Stack>
            {groups.current ? (
              <section aria-labelledby="learning-current">
                <SectionTitle id="learning-current">
                  {t('careerCenter.learning.currentTitle')}
                </SectionTitle>
                <CareerLessonRow lesson={groups.current} variant="current" />
              </section>
            ) : (
              <AllDone>
                <CareerIcon name="check" size={24} />
                {t('careerCenter.learning.allDone')}
              </AllDone>
            )}
            {groups.open.map((lesson) => (
              <CareerLessonRow key={lesson.id} lesson={lesson} />
            ))}
            {groups.locked.length ? (
              <section aria-labelledby="learning-next">
                <SectionTitle id="learning-next">
                  <CareerIcon name="lock" size={20} />
                  {t('careerCenter.learning.nextTitle')}
                </SectionTitle>
                <Stack>
                  {groups.locked.map((lesson) => (
                    <CareerLessonRow key={lesson.id} lesson={lesson} />
                  ))}
                </Stack>
              </section>
            ) : null}
          </Stack>
        ) : (
          <Stack>
            {(tab === 'done' ? groups.completed : groups.bookmarked).map((lesson) => (
              <CareerLessonRow key={lesson.id} lesson={lesson} />
            ))}
            {!(tab === 'done' ? groups.completed : groups.bookmarked).length ? (
              <Empty>
                {tab === 'done'
                  ? t('careerCenter.learning.tabs.doneEmpty')
                  : t('careerCenter.learning.tabs.savedEmpty')}
              </Empty>
            ) : null}
          </Stack>
        )}
      </Panel>
    </PageWrapper>
  );
});

const PageWrapper = styled.div`
  width: 100%;
  padding-top: 24px;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const AllDone = styled.p`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0;
  padding: ${careerLayout.cardPadding}px;
  border-radius: ${careerLayout.cardRadius}px;
  background: ${Colors.bg_gray};
  ${careerType.bodyStrong()};
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

const Empty = styled.p`
  margin: 0;
  padding: 48px 0;
  text-align: center;
  color: ${Colors.gray_dark};
  ${careerType.body()};
`;

const Header = styled.header`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const TitleIcon = styled.span<{ $bg: string; $fg: string }>`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: ${({ $bg }) => $bg};
  color: ${({ $fg }) => $fg};
`;

const PageTitle = styled.h1`
  margin: 0;
  ${careerType.h1()};
`;

const Progress = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  max-width: 560px;
`;

const Bar = styled.span`
  flex: 1 1 auto;
  height: 10px;
  border-radius: 999px;
  background: ${Colors.bg_gray};
  overflow: hidden;
`;

const BarFill = styled.span<{ $fg: string }>`
  display: block;
  height: 100%;
  border-radius: 999px;
  background: ${({ $fg }) => $fg};
`;

const Counter = styled.p`
  flex: none;
  margin: 0;
  ${careerType.bodyStrong()};
`;

const Tabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 24px;
  padding: 6px;
  border-radius: 16px;
  background: ${Colors.bg_gray};
  width: fit-content;
  max-width: 100%;

  ${mobile} {
    flex-wrap: nowrap;
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const Tab = styled.button<{ $active: boolean; $bg: string; $fg: string }>`
  flex: none;
  display: inline-flex;
  white-space: nowrap;
  align-items: center;
  gap: 8px;
  min-height: ${careerLayout.hitTarget}px;
  padding: 0 16px;
  border: 0;
  border-radius: 12px;
  background: ${({ $active }) => ($active ? Colors.white : 'transparent')};
  color: ${({ $active, $fg }) => ($active ? $fg : Colors.black)};
  box-shadow: ${({ $active }) => ($active ? '0 2px 8px rgba(7, 7, 7, 0.1)' : 'none')};
  cursor: pointer;
  ${careerType.bodyStrong()};

  &:hover {
    background: ${Colors.white};
  }
`;

const TabCount = styled.span`
  min-width: 24px;
  padding: 0 8px;
  border-radius: 999px;
  background: ${Colors.bg_gray};
  color: ${Colors.black};
  text-align: center;
  ${careerType.metaStrong()};
`;

const Panel = styled.div`
  width: 100%;
`;

const SectionTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 16px 0 12px;
  ${careerType.h2()};
`;
